import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, Download, FolderOpen, Pause, Play, RotateCcw, ShieldCheck, Square, WifiOff } from 'lucide-react'
import { isDesktopRuntime } from '@/services/desktopAuth'
import { probeDesktopCloud } from '@/services/desktopCloud'
import {
  checkDesktopUpdate,
  cancelHandsAction,
  enableHandsAuthority,
  getDesktopCacheStatus,
  getHandsAuthority,
  getHandsRuntimeStatus,
  installDesktopUpdate,
  restartHandsRuntime,
  startHandsRuntime,
  setHandsBrowserControl,
  setHandsPaused,
  type DesktopUpdateCandidate,
  type DesktopDiagnosticReport,
  type HandsAuthority,
  type HandsRuntimeStatus,
} from '@/services/desktopRuntime'

import { NativeApplicationAccess } from './NativeApplicationAccess'

type RuntimeControl = 'start' | 'restart'

export const DesktopRuntimeStatus = () => {
  const isDesktop = isDesktopRuntime()
  const [cloudReachable, setCloudReachable] = useState(() => (
    typeof navigator === 'undefined' ? true : navigator.onLine
  ))
  const cloudProbeGeneration = useRef(0)
  const [hands, setHands] = useState<HandsRuntimeStatus | null>(null)
  const [authority, setAuthority] = useState<HandsAuthority | null>(null)
  const [cache, setCache] = useState<DesktopDiagnosticReport['cache'] | null>(null)
  const [update, setUpdate] = useState<DesktopUpdateCandidate | null>(null)
  const [updateCheckFailed, setUpdateCheckFailed] = useState(false)
  const [installing, setInstalling] = useState(false)
  const [installFailed, setInstallFailed] = useState(false)
  const [runtimeControl, setRuntimeControl] = useState<RuntimeControl | null>(null)
  const [runtimeControlFailed, setRuntimeControlFailed] = useState(false)
  const [authorityControl, setAuthorityControl] = useState<
    'enable' | 'pause' | 'resume' | 'enable-browser' | 'disable-browser' | null
  >(null)
  const [authorityControlFailed, setAuthorityControlFailed] = useState(false)
  const [actionControl, setActionControl] = useState<string | null>(null)
  const [actionControlFailed, setActionControlFailed] = useState(false)

  const refreshHands = useCallback(async () => {
    if (!isDesktop) return
    try {
      setHands(await getHandsRuntimeStatus())
    } catch {
      setHands({
        status: 'blocked',
        desiredRunning: false,
        restartCount: 0,
        failureCode: 'supervisor_unavailable',
        clientVersion: null,
        protocolVersion: null,
        localActionsEnabled: false,
        activeAction: null,
      })
    }
  }, [isDesktop])

  const refreshCloud = useCallback(async () => {
    if (!isDesktop) return
    const generation = ++cloudProbeGeneration.current
    if (!navigator.onLine) {
      setCloudReachable(false)
      return
    }

    const reachable = await probeDesktopCloud()
    if (generation === cloudProbeGeneration.current) {
      setCloudReachable(reachable && navigator.onLine)
    }
  }, [isDesktop])

  const checkRuntime = useCallback(async () => {
    if (!isDesktop) return
    setUpdate(null)
    setUpdateCheckFailed(false)

    const [handsResult, authorityResult, cacheResult, updateResult] = await Promise.allSettled([
      getHandsRuntimeStatus(),
      getHandsAuthority(),
      getDesktopCacheStatus(),
      checkDesktopUpdate(),
    ])
    if (handsResult.status === 'fulfilled') {
      setHands(handsResult.value)
    } else {
      setHands({
        status: 'blocked',
        desiredRunning: false,
        restartCount: 0,
        failureCode: 'supervisor_unavailable',
        clientVersion: null,
        protocolVersion: null,
        localActionsEnabled: false,
        activeAction: null,
      })
    }
    if (authorityResult.status === 'fulfilled') {
      setAuthority(authorityResult.value)
    }
    if (cacheResult.status === 'fulfilled') {
      setCache(cacheResult.value)
    } else {
      setCache({
        status: 'unavailable',
        encrypted: true,
        schemaVersion: null,
        resourceStoreSchemaVersion: null,
        cursorSchemaVersion: null,
        mutationJournalSchemaVersion: null,
        mutationEntrySchemaVersion: null,
        resourceCount: 0,
        pendingMutationCount: 0,
        migrationStatus: 'blocked',
        syncStatus: 'not_synchronized',
        serverVersion: 0,
        synchronizedAtUnixSeconds: null,
        authorizationLeaseStatus: 'not_issued',
        authorizationLeaseExpiresAtUnixSeconds: null,
        cacheReadsAuthorized: false,
      })
    }
    if (updateResult.status === 'fulfilled') {
      setUpdate(updateResult.value.update)
    } else {
      setUpdateCheckFailed(true)
    }
  }, [isDesktop])

  useEffect(() => {
    if (!isDesktop) return undefined
    void checkRuntime()
    void refreshCloud()
    const markOnline = () => {
      void refreshCloud()
      void checkRuntime()
    }
    const markOffline = () => {
      cloudProbeGeneration.current += 1
      setCloudReachable(false)
    }
    window.addEventListener('online', markOnline)
    window.addEventListener('offline', markOffline)
    const runtimePoll = window.setInterval(() => void refreshHands(), 1_000)
    const cloudPoll = window.setInterval(() => void refreshCloud(), 15_000)
    return () => {
      cloudProbeGeneration.current += 1
      window.removeEventListener('online', markOnline)
      window.removeEventListener('offline', markOffline)
      window.clearInterval(runtimePoll)
      window.clearInterval(cloudPoll)
    }
  }, [checkRuntime, isDesktop, refreshCloud, refreshHands])

  useEffect(() => {
    setActionControlFailed(false)
  }, [hands?.activeAction?.commandId])

  const install = async () => {
    if (!update || installing) return
    setInstalling(true)
    setInstallFailed(false)
    try {
      await installDesktopUpdate(update.version, update.releaseEpoch)
    } catch {
      setInstallFailed(true)
      setInstalling(false)
    }
  }

  const controlHands = async (control: RuntimeControl) => {
    if (runtimeControl) return
    setRuntimeControl(control)
    setRuntimeControlFailed(false)
    try {
      if (control === 'start') await startHandsRuntime()
      if (control === 'restart') await restartHandsRuntime()
      await refreshHands()
    } catch {
      setRuntimeControlFailed(true)
    } finally {
      setRuntimeControl(null)
    }
  }

  const controlAuthority = async (
    control: 'enable' | 'pause' | 'resume' | 'enable-browser' | 'disable-browser',
  ) => {
    if (authorityControl) return
    setAuthorityControl(control)
    setAuthorityControlFailed(false)
    try {
      const next = control === 'enable'
        ? await enableHandsAuthority()
        : control === 'enable-browser' || control === 'disable-browser'
          ? await setHandsBrowserControl(control === 'enable-browser')
          : await setHandsPaused(control === 'pause')
      setAuthority(next)
      await refreshHands()
    } catch {
      setAuthorityControlFailed(true)
    } finally {
      setAuthorityControl(null)
    }
  }

  const stopActiveAction = async (commandId: string) => {
    if (actionControl) return
    setActionControl(commandId)
    setActionControlFailed(false)
    setHands((current) => (
      current?.activeAction?.commandId === commandId
        ? {
            ...current,
            activeAction: { ...current.activeAction, status: 'cancelling' },
          }
        : current
    ))
    try {
      await cancelHandsAction(commandId)
      await refreshHands()
    } catch {
      try {
        const latest = await getHandsRuntimeStatus()
        setHands(latest)
        setActionControlFailed(latest.activeAction?.commandId === commandId)
      } catch {
        setActionControlFailed(true)
      }
    } finally {
      setActionControl(null)
    }
  }

  const activeActionLabel = hands?.activeAction
    ? {
        browser_navigate: 'Opening an approved web page',
        browser_read_page: 'Reading an approved web page',
        browser_snapshot: 'Understanding the current browser page',
        browser_scroll: 'Moving through the current browser page',
        browser_click: 'Using an approved browser control',
        browser_fill: 'Filling an approved browser field',
        fs_list: 'Listing your Ricald folder',
        fs_read_text: 'Reading a file in your Ricald folder',
        fs_write_text: 'Writing a file in your Ricald folder',
        vault_list_markdown: 'Checking your local knowledge folder',
        vault_read_markdown: 'Reading an approved local knowledge note',
        vault_publish_markdown: 'Saving an approved local knowledge note',
        native_observe: 'Reading the connected application',
        native_set_value: 'Filling the selected application field',
        native_press: 'Using the approved application control',
      }[hands.activeAction.actionType]
    : null
  const localRecoveryRequired = cache !== null && cache.status !== 'ready'

  if (!isDesktop) return null

  return (
    <div className="space-y-2 px-6 pt-4" aria-label="Desktop runtime status">
      {!cloudReachable && (
        <section
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-[var(--state-warning)]/35 bg-[var(--state-warning)]/10 px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]" />
          <div>
            <p className="font-semibold">Cloud connection unavailable</p>
            <p className="text-[var(--text-secondary)]">
              Ricald cannot currently confirm cloud availability. Cloud-backed reads or changes may fail until the connection returns.
            </p>
          </div>
        </section>
      )}

      {localRecoveryRequired && (
        <section
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-[var(--state-warning)]/35 bg-[var(--state-warning)]/10 px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-warning)]" />
          <div>
            <p className="font-semibold">
              {cache.status === 'quarantined' ? 'Local recovery is required' : 'Local safety check unavailable'}
            </p>
            <p className="text-[var(--text-secondary)]">
              {cache.status === 'quarantined'
                ? 'Ricald preserved the encrypted local state and locked local work. Install a compatible signed update when offered below; cloud work remains available.'
                : 'Ricald could not verify the local cache, so local work is locked. Restart Desktop or install a compatible signed update when offered; cloud work remains available.'}
            </p>
          </div>
        </section>
      )}

      {!localRecoveryRequired && authority && !authority.configured && (
        <section className="flex items-center justify-between gap-5 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-5 py-4 text-[15px] text-[var(--text-primary)]">
          <div>
            <p className="font-semibold">Let Ricald work on this computer</p>
            <p className="mt-1 text-[var(--text-secondary)]">
              Understand websites and create finished files in your Ricald folder. You can add browser control separately.
            </p>
            {authorityControlFailed && (
              <p role="alert" className="mt-2 font-medium text-[var(--state-danger)]">
                Local work was not enabled. Check your connection and try again.
              </p>
            )}
          </div>
          <button
            type="button"
            disabled={authorityControl !== null}
            onClick={() => void controlAuthority('enable')}
            className="shrink-0 rounded-lg bg-[var(--accent)] px-4 py-2 font-semibold text-[var(--text-on-accent)] disabled:cursor-wait disabled:opacity-65"
          >
            {authorityControl === 'enable' ? 'Connecting…' : 'Enable local work'}
          </button>
        </section>
      )}

      {!localRecoveryRequired && authority?.configured && (
        <section className="flex items-center justify-between gap-5 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-5 py-4 text-[15px] text-[var(--text-primary)]">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck className={`h-5 w-5 ${authority.paused ? 'text-[var(--text-secondary)]' : 'text-[var(--state-success)]'}`} />
              <p className="font-semibold">
                {authority.paused
                  ? 'Local work is paused'
                  : hands?.status === 'running' && hands.localActionsEnabled
                    ? 'Ricald can work on this computer'
                    : 'Local work is allowed'}
              </p>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[13px] font-medium text-[var(--text-secondary)]">
              {authority.browserRead && <span className="rounded-full bg-[var(--bg-hover)] px-2.5 py-1">Browser understanding</span>}
              {authority.browserControl && <span className="rounded-full bg-[var(--bg-hover)] px-2.5 py-1">Browser control</span>}
              {authority.workspaceRead && authority.workspaceWrite && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-hover)] px-2.5 py-1">
                  <FolderOpen className="h-3.5 w-3.5" /> {authority.workspaceName} folder
                </span>
              )}
              {authority.resourceGrants.map((grant) => (
                <span key={grant.id} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-hover)] px-2.5 py-1">
                  <FolderOpen className="h-3.5 w-3.5" /> {grant.label}
                </span>
              ))}
            </div>
            <button
              type="button"
              disabled={authorityControl !== null}
              onClick={() => void controlAuthority(
                authority.browserControl ? 'disable-browser' : 'enable-browser',
              )}
              className="mt-3 text-[13px] font-semibold text-[var(--accent)] hover:underline disabled:cursor-wait disabled:opacity-65"
            >
              {authorityControl === 'enable-browser' || authorityControl === 'disable-browser'
                ? 'Updating browser access…'
                : authority.browserControl
                  ? 'Turn off browser control'
                  : 'Enable browser control'}
            </button>
            {!authority.browserControl && (
              <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Lets Ricald click and fill in its isolated browser. Consequential actions still ask for exact approval.
              </p>
            )}
            {authorityControlFailed && (
              <p role="alert" className="mt-2 font-medium text-[var(--state-danger)]">
                The device control did not complete. Try again.
              </p>
            )}
          </div>
          <button
            type="button"
            disabled={authorityControl !== null}
            onClick={() => void controlAuthority(authority.paused ? 'resume' : 'pause')}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 font-semibold hover:bg-[var(--bg-hover)] disabled:cursor-wait disabled:opacity-65"
          >
            {authority.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            {authorityControl ? 'Updating…' : authority.paused ? 'Resume' : 'Pause'}
          </button>
        </section>
      )}

      {!localRecoveryRequired && authority?.configured && <NativeApplicationAccess authority={authority} onChange={setAuthority} />}

      {hands?.status === 'running' && hands.activeAction && activeActionLabel && (
        <section
          role="status"
          aria-live="polite"
          className="flex items-center justify-between gap-5 rounded-xl border border-[var(--accent)]/35 bg-[var(--accent)]/10 px-5 py-4 text-[15px] text-[var(--text-primary)]"
        >
          <div className="min-w-0">
            <p className="font-semibold">
              {hands.activeAction.status === 'cancelling' ? 'Stopping local action…' : activeActionLabel}
            </p>
            <p className="mt-1 text-[var(--text-secondary)]">
              {hands.activeAction.status === 'cancelling'
                ? 'Ricald will record the outcome as uncertain if completion cannot be verified.'
                : 'This action is running on this computer under your current grants.'}
            </p>
            {actionControlFailed && (
              <p role="alert" className="mt-2 font-medium text-[var(--state-danger)]">
                This action could not be stopped. You can still pause all local work.
              </p>
            )}
          </div>
          <button
            type="button"
            disabled={hands.activeAction.status === 'cancelling' || actionControl !== null}
            onClick={() => void stopActiveAction(hands.activeAction!.commandId)}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 font-semibold hover:bg-[var(--bg-hover)] disabled:cursor-wait disabled:opacity-65"
          >
            <Square className="h-3.5 w-3.5 fill-current" />
            {hands.activeAction.status === 'cancelling' || actionControl ? 'Stopping…' : 'Stop this action'}
          </button>
        </section>
      )}

      {!localRecoveryRequired && authority?.configured && (hands?.status === 'starting' || hands?.status === 'backoff') && (
        <section
          role="status"
          className="flex items-start gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-[var(--accent)]" />
          <div>
            <p className="font-semibold">
              {hands.status === 'starting' ? 'Starting local work' : 'Reconnecting local work'}
            </p>
            <p className="text-[var(--text-secondary)]">
              {hands.status === 'starting'
                ? 'Ricald is connecting this computer.'
                : 'A local connection stopped. Ricald is retrying safely.'}
            </p>
          </div>
        </section>
      )}

      {!localRecoveryRequired && hands?.status === 'blocked' && authority?.configured && (
        <section
          role="alert"
          className="flex items-start justify-between gap-4 rounded-xl border border-[var(--state-danger)]/30 bg-[var(--state-danger)]/10 px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-danger)]" />
            <div>
              <p className="font-semibold">
                {hands.failureCode === 'browser_unavailable'
                  ? 'Google Chrome is required for web work'
                  : hands.failureCode === 'cloud_unreachable'
                    ? 'This computer could not reach Ricald'
                    : hands.failureCode === 'invalid_config'
                      ? 'Reconnect local work'
                      : 'Local work could not start'}
              </p>
              <p className="text-[var(--text-secondary)]">
                Local work is off. Your cloud work is unchanged.
              </p>
              {runtimeControlFailed && (
                <p role="alert" className="mt-1 font-medium text-[var(--state-danger)]">
                  The retry did not complete. Local actions remain disabled.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={runtimeControl !== null}
            onClick={() => void controlHands('start')}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
          >
            <RotateCcw className="h-4 w-4" />
            {runtimeControl === 'start' ? 'Retrying…' : 'Retry'}
          </button>
        </section>
      )}

      {!localRecoveryRequired && hands?.status === 'locked_out' && authority?.configured && (
        <section
          role="alert"
          className="flex items-start justify-between gap-4 rounded-xl border border-[var(--state-danger)]/30 bg-[var(--state-danger)]/10 px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[var(--state-danger)]" />
            <div>
              <p className="font-semibold">Local work needs attention</p>
              <p className="text-[var(--text-secondary)]">
                Ricald stopped after repeated connection failures. Your cloud work is unchanged.
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={runtimeControl !== null}
            onClick={() => void controlHands('restart')}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-3 py-2 font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-hover)] disabled:cursor-wait disabled:opacity-65"
          >
            <RotateCcw className="h-4 w-4" />
            {runtimeControl === 'restart' ? 'Restarting…' : 'Try again'}
          </button>
        </section>
      )}

      {!localRecoveryRequired && hands?.status === 'stopped' && authority?.configured && !authority.paused && (
        <section
          role="status"
          className="flex items-start justify-between gap-4 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <div>
            <p className="font-semibold">Local work is off</p>
            <p className="text-[var(--text-secondary)]">
              Start it to use the permissions shown above. Your cloud work is unchanged.
            </p>
          </div>
          <button
            type="button"
            disabled={runtimeControl !== null}
            onClick={() => void controlHands('start')}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[var(--accent)] px-4 py-2 font-semibold text-[var(--text-on-accent)] disabled:cursor-wait disabled:opacity-65"
          >
            <Play className="h-4 w-4" />
            {runtimeControl === 'start' ? 'Starting…' : 'Start local work'}
          </button>
        </section>
      )}

      {updateCheckFailed && cloudReachable && (
        <section
          role="status"
          className="flex items-start gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-card)] px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-secondary)]" />
          <div>
            <p className="font-semibold">Update verification is temporarily unavailable</p>
            <p className="text-[var(--text-secondary)]">
              The current signed build remains active. No update will install without a verified release policy.
            </p>
          </div>
        </section>
      )}

      {update && (
        <section
          role="status"
          className="flex items-start justify-between gap-4 rounded-xl border border-[var(--accent)]/35 bg-[var(--accent)]/10 px-4 py-3 text-[15px] leading-6 text-[var(--text-primary)]"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[var(--accent)]" />
            <div>
              <p className="font-semibold">
                {update.recovery
                  ? 'Authorized recovery build'
                  : update.critical
                    ? `Critical update — Ricald ${update.version}`
                    : `Ricald ${update.version} is ready`}
              </p>
              <p className="text-[var(--text-secondary)]">
                {update.notes || `Ricald Computer ${update.sidecarVersion} passed compatibility checks.`}
              </p>
              {installFailed && (
                <p role="alert" className="mt-1 font-medium text-[var(--state-danger)]">
                  Installation stopped safely. The current build is unchanged; try again when ready.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={installing}
            onClick={() => void install()}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[var(--accent)] px-4 py-2 font-semibold text-[var(--text-on-accent)] disabled:cursor-wait disabled:opacity-65"
          >
            <Download className="h-4 w-4" />
            {installing ? 'Verifying…' : 'Install and restart'}
          </button>
        </section>
      )}
    </div>
  )
}

export default DesktopRuntimeStatus
